import { Editor, EditorCore, EditorPlugin } from 'roosterjs-editor-core';
import { PerfData } from './PerfData';
import { PluginEvent } from 'roosterjs-editor-types';

const PERF_DATA_KEY = 'PERF_DATA';
const TOTAL_TIME_NAME = 'TOTAL_TIME';
const START_MARK_SUFFEX = '_S';
const END_MARK_SUFFEX = '_E';
const CHECK_EXCLUSIVE_HANDLING_MARK_SUFFEX = '_C';

export function getPerfData(editor: Editor | EditorCore): PerfData {
    if (editor instanceof Editor) {
        return editor.getCustomData(PERF_DATA_KEY, createPerfData);
    } else {
        return editor.api.getCustomData(editor, PERF_DATA_KEY, createPerfData);
    }
}

function createPerfData(): PerfData {
    return {};
}

export function triggerEventWithMeasure(
    core: EditorCore,
    pluginEvent: PluginEvent,
    broadcast: boolean
) {
    let eventType = pluginEvent.eventType;
    let perf = core.document.defaultView.performance;
    let measureNames = [];

    measure(
        TOTAL_TIME_NAME,
        perf,
        () => {
            if (
                broadcast ||
                !core.plugins.some(plugin =>
                    handledEventExclusivelyWithMeasure(pluginEvent, plugin, perf, measureNames)
                )
            ) {
                core.plugins.forEach(plugin => {
                    if (plugin.onPluginEvent) {
                        measure(
                            plugin.getName() + '_' + eventType,
                            perf,
                            () => plugin.onPluginEvent(pluginEvent),
                            measureNames
                        );
                    }
                });
            }
        },
        measureNames
    );

    logEventHandleTimeIfNecessary(core, TOTAL_TIME_NAME, perf, measureNames);
}

function handledEventExclusivelyWithMeasure(
    event: PluginEvent,
    plugin: EditorPlugin,
    perf: Performance,
    measureNames: string[]
): boolean {
    if (!plugin.onPluginEvent || !plugin.willHandleEventExclusively) {
        return false;
    }

    let eventType = event.eventType;
    let result: boolean;
    measure(
        plugin.getName() + '_' + eventType + CHECK_EXCLUSIVE_HANDLING_MARK_SUFFEX,
        perf,
        () => {
            result = plugin.willHandleEventExclusively(event);
        },
        measureNames
    );

    if (result) {
        measure(plugin.getName(), perf, () => plugin.onPluginEvent(event), measureNames);
    }

    return result;
}

/**
 * Measure the execution time of a callback function with two perf marks
 * If the name for this measure is "Test", then the two marks added will be:
 * Test_S
 * Test_E
 * Then it will add a measure with name 'Test' to measure the time between these two marks
 * measure result of these two marks with name 'Test'
 * @param name Name of the measure
 * @param perf Performance object
 * @param callback The callback function to measure
 * @param names Names of existing measures, name of current measure will be added into this array.
 */
function measure(name: string, perf: Performance, callback: () => void, names: string[]) {
    // For browser versions that don't support Perf.mark (e.g. safari < 11), don't measure
    // performance, and instead just call through.
    if (!perf.mark) {
        return callback();
    }
    let start = name + START_MARK_SUFFEX;
    let end = name + END_MARK_SUFFEX;
    perf.mark(start);
    callback();
    perf.mark(end);
    try {
        perf.measure(name, start, end);
        names.push(name);
        perf.clearMarks(start);
        perf.clearMarks(end);
    } catch {}
}

function logEventHandleTimeIfNecessary(
    core: EditorCore,
    totalName: string,
    perf: Performance,
    measuredNames: string[]
) {
    // For browser versions that don't support Perf.getEntriesByName (e.g. safari < 11), do nothing
    if (!perf.getEntriesByName) {
        return;
    }
    let totals = <PerformanceEntry[]>perf.getEntriesByName(totalName);
    let total = totals ? totals[0] : null;

    if (total && total.duration >= 10) {
        let perfData = getPerfData(core);
        for (let measureName of measuredNames) {
            if (measureName == totalName) {
                continue;
            }

            let measures = <PerformanceEntry[]>perf.getEntriesByName(measureName);
            let measure = measures && measures[0];

            if (!measure) {
                continue;
            }

            let data = perfData[measure.name];

            if (measure.duration > 1) {
                if (data) {
                    data.count++;
                    data.totalTime += measure.duration;
                } else {
                    perfData[measure.name] = {
                        count: 1,
                        totalTime: measure.duration,
                    };
                }
            }
        }
    }

    measuredNames.forEach(name => perf.clearMeasures(name));
}
